import { useEffect, useState } from "react";
import { StatusCatalog } from "@interfaces/response";
import { RootState } from "@store/store";
import { useSelector } from "react-redux";
import { getConfigName, getIncomeBranchStatusToDiscard, getLegalRepStatusToDiscard } from "@helpers/funciones";
import { STATUS_BURO_BLOQUEADO, STATUS_SIN_COBERTURA } from "@helpers/constantes";

/**
 * El backend puede mandar `url` sin la diagonal inicial (`v2/producto/...`). React Router la toma
 * como ruta relativa y la encima sobre la actual, así que se normaliza a absoluta.
 */
const withAbsoluteUrl = (item: StatusCatalog | undefined | null): StatusCatalog | null => {
  if (!item) return null;
  if (!item.url || item.url.startsWith("/")) return item;
  return { ...item, url: `/${item.url}` };
};

const useLoanStatus = (status: string) => {
  const [currentStatus, setCurrentStatus] = useState<StatusCatalog | null>(null);
  const [nextStatus, setNextStatus] = useState<StatusCatalog | null>(null);
  const [previousStatus, setPreviousStatus] = useState<StatusCatalog | null>(null);

  const statusConfig = useSelector((state: RootState) => state.app.status_config);
  const incomeOption = useSelector((state: RootState) => state.register.income_declaration_option);
  const taxSystemType = useSelector((state: RootState) => state.register.application.tax_system_type);
  const shareholderIsLegalRep = useSelector((state: RootState) => state.register.shareholder_is_legal_rep);

  useEffect(() => {
    if (status === "INIT") {
      setPreviousStatus(null);
      setNextStatus(withAbsoluteUrl(statusConfig[0]));
      setCurrentStatus(null);
    } else {
      // Además de buró bloqueado y sin cobertura (no son pasos de la secuencia), la vía de ingresos que no se eligió
      // en `DecisionIngreso` (si la empresa la tiene configurada en `decisionIngresoConfig`) y, si el accionista
      // mayoritario es también el representante legal, las 3 pantallas de este último.
      const status_to_discard = [
        STATUS_BURO_BLOQUEADO,
        STATUS_SIN_COBERTURA,
        ...getIncomeBranchStatusToDiscard(
          statusConfig,
          getConfigName(window.location.href),
          incomeOption,
          taxSystemType,
        ),
        ...getLegalRepStatusToDiscard(statusConfig, shareholderIsLegalRep),
      ];
      const status_filteres = statusConfig.filter((item) => !status_to_discard.includes(item.code));
      const currentIndex = status_filteres.findIndex((item) => item.code === status);

      if (currentIndex !== -1) {
        const previousIndex = currentIndex - 1;
        const nextIndex = currentIndex + 1;

        if (previousIndex !== -1) {
          setPreviousStatus(withAbsoluteUrl(status_filteres[previousIndex]));
        }
        if (nextIndex !== status_filteres.length) {
          setNextStatus(withAbsoluteUrl(status_filteres[nextIndex]));
        }
        setCurrentStatus(withAbsoluteUrl(status_filteres[currentIndex]));
      } else {
        setCurrentStatus(null);
        setNextStatus(null);
        setPreviousStatus(null);
      }
    }
  }, [status, statusConfig, incomeOption, taxSystemType, shareholderIsLegalRep]);

  return {
    currentStatus,
    nextStatus,
    previousStatus,
  };
};

export default useLoanStatus;
